import React, { FC, useEffect } from 'react';
import Cookies from 'js-cookie';
import { withOptimizely, WithOptimizelyProps } from 'shared/optimizely';
import useLiveRamp from 'shared/hooks/optimizely/useLiveRamp';
import Head from 'next/head';
import { IWindowWithLiveRamp, LiveRampCommands } from './IWindowWithLiveRamp';
import { addDays } from 'date-fns';
import { IConsent, ConsentType } from './ConsentTypes';

import defaultHeader from './assets/header.html.txt';
import experimentHeaderButton from './assets/header-CRME-1097-button.html.txt';
import experimentHeaderControl from './assets/header-CRME-1097-control.html.txt';
import experimentHeaderIcon from './assets/header-CRME-1097-icon.html.txt';
import experimentHeaderText from './assets/header-CRME-1097-text.html.txt';
import liveRampScript from './assets/gdpr-liveramp.js.txt';

const cookies = Cookies.withConverter({
    write: (value) => value.toString()
});
declare const window: IWindowWithLiveRamp;
const LiveRampVersion = 2;

const zooplaDomain = 'zoopla.co.uk';
const cookieConsentsKey = 'cookie_consents';

const createConsentObject = (): IConsent => ({
    schemaVersion: 4,
    content: {
        brand: 1,
        consents: [
            {
                apiVersion: 1,
                stored: false,
                date: new Date().toUTCString(),
                categories: [
                    {
                        id: ConsentType.Necessary,
                        consentGiven: false
                    },
                    {
                        id: ConsentType.Advertising,
                        consentGiven: false
                    },
                    {
                        id: ConsentType.Functionality,
                        consentGiven: false
                    }
                ]
            }
        ]
    }
});

const getCookieDomain = () => {
    const { hostname } = window.location;
    const isZooplaDomain = hostname.includes(zooplaDomain);

    return isZooplaDomain ? zooplaDomain : hostname;
};

const setConsentCookie = () => {
    const consent = cookies.get(cookieConsentsKey);
    const cookieObject = createConsentObject();

    if (consent) {
        cookies.remove(cookieConsentsKey);
    }

    cookies.set(cookieConsentsKey, cookieObject, {
        expires: addDays(Date.now(), 1),
        domain: getCookieDomain(),
        path: '/'
    });
};

const addLiveRampEventListener = () =>
    window.__tcfapi &&
    window.__tcfapi(
        LiveRampCommands.AddEventListener,
        LiveRampVersion,
        (tcData: unknown, success: boolean) => {
            console.info('LiveRamp event detected...');
            console.info('event: ', tcData, success);
        }
    );

const removeLiveRampEventListener = () =>
    window.__tcfapi &&
    window.__tcfapi(
        LiveRampCommands.RemoveEventListener,
        LiveRampVersion,
        (result: boolean) => {
            console.info(
                `LiveRamp listeners removed: ${result ? 'true' : 'false'}`
            );
        }
    );

const variationMap: Record<string, string> = {
    control: experimentHeaderControl,
    icon: experimentHeaderIcon,
    text: experimentHeaderText,
    button: experimentHeaderButton
};

const Header: FC<WithOptimizelyProps> = ({ optimizely }) => {
    const _useLiveRamp = useLiveRamp(optimizely);
    const optimizelyVariation = optimizely?.activate(
        'crme_1097_sign_in_mobile_header_link'
    );

    let header = defaultHeader;

    if (optimizelyVariation && variationMap[optimizelyVariation]) {
        header = variationMap[optimizelyVariation];
    }

    useEffect(() => {
        if (_useLiveRamp) {
            setConsentCookie();
            addLiveRampEventListener();
        }

        return () => {
            if (_useLiveRamp) {
                removeLiveRampEventListener();
            }
        };
    }, [_useLiveRamp]);

    return (
        <>
            {_useLiveRamp && (
                <Head>
                    <script
                        id="liveramp-tag"
                        dangerouslySetInnerHTML={{
                            __html: liveRampScript
                        }}
                    />
                </Head>
            )}
            <div
                data-testid="universal-header"
                dangerouslySetInnerHTML={{
                    __html: header
                }}
            />
        </>
    );
};

export default withOptimizely(Header);
